import { useEffect, useState } from 'react';
import BaseApi from '../../../services/BaseApi';
import Modal from '../../Modal';
import AddressSearch from './AddressSearch';
import EvidenceUpload from './EvidenceUpload';

import { submissionId, evidenceFields } from './viabilityDefinitions';
import ViabilityPendingAction, { ViabilityPendingPanel, ViabilityCorrectionForm } from './ViabilityPendings';
export { submissionId, evidenceFields } from './viabilityDefinitions';
export { default as ViabilityPendingAction } from './ViabilityPendings';

export function ViabilityChoice({ proposalId, onConfirm }: { proposalId: string; onConfirm: (type: string, id: string) => Promise<void> }) {
  const [options, setOptions] = useState<any>();
  const [type, setType] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(submissionId);
  useEffect(() => {
    BaseApi.get(`api/proposal/${proposalId}/viability/options`).then(setOptions).catch(e => setError(Modal.getErrorMessageText(e)));
  }, [proposalId]);
  async function confirm() {
    setBusy(true); setError('');
    try { await onConfirm(type, id); }
    catch (e) { setError(Modal.getErrorMessageText(e)); setBusy(false); }
  }
  return <div>
    <p>Escolha o tipo de viabilidade para continuar.</p>
    {options && <>
      <p>Potência total dos inversores: <strong>{options.inverterPowerKw} kW</strong></p>
      <label className="d-block border rounded p-3 mb-2">
        <input type="radio" name="viabilityType" value="RAPIDA" checked={type === 'RAPIDA'} disabled={busy || !options.quickAvailable} onChange={() => { setType('RAPIDA'); setId(submissionId()); }} />{' '}
        <strong>Viabilidade Rápida — R$ 250,00</strong><br />Até 10 kW de inversor. Requer envio dos dados do local antes do pagamento do sinal.
      </label>
      <label className="d-block border rounded p-3 mb-3">
        <input type="radio" name="viabilityType" value="PERSONALIZADA" checked={type === 'PERSONALIZADA'} disabled={busy} onChange={() => { setType('PERSONALIZADA'); setId(submissionId()); }} />{' '}
        <strong>Viabilidade Personalizada/Customizada — R$ 750,00</strong><br />Segue diretamente para o pagamento do sinal.
      </label>
    </>}
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    <button type="button" className="btn btn-primary" disabled={!type || busy} onClick={confirm}>{busy ? 'Confirmando...' : 'Confirmar e continuar'}</button>
  </div>;
}

export function LocalDataForm({ proposalId, advanceToPayment = true }: { proposalId: string; advanceToPayment?: boolean }) {
  const [study, setStudy] = useState<any>();
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [roof, setRoof] = useState('');
  const [panel, setPanel] = useState('');
  const [files, setFiles] = useState<Record<string, File>>({});
  const [error, setError] = useState('');
  const [showAddressSearch, setShowAddressSearch] = useState(false);
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(submissionId);
  useEffect(() => {
    BaseApi.get(`api/proposal/${proposalId}/viability`).then((value: any) => {
      setStudy(value);
      setLatitude(value.location?.latitude?.toString() ?? '');
      setLongitude(value.location?.longitude?.toString() ?? '');
      setRoof(value.roofStructure ?? '');
      setPanel(value.hasInternalBreakerPanel === true ? 'yes' : value.hasInternalBreakerPanel === false ? 'no' : '');
    }).catch(e => setError(Modal.getErrorMessageText(e)));
  }, [proposalId]);
  function changed() { setId(submissionId()); setSuccess(''); }
  function locate() {
    if (!navigator.geolocation) { setError('Localização indisponível. Informe as coordenadas manualmente.'); return; }
    navigator.geolocation.getCurrentPosition(position => {
      setLatitude(String(position.coords.latitude)); setLongitude(String(position.coords.longitude)); changed(); setError('');
    }, () => setError('Não foi possível obter a localização. Informe as coordenadas manualmente.'), { enableHighAccuracy: true });
  }
  async function submit(event: React.SyntheticEvent) {
    event.preventDefault(); setError('');
    const data = new FormData();
    data.append('submissionId', id); data.append('viabilityStudyId', study.viabilityStudyId);
    data.append('latitude', latitude); data.append('longitude', longitude);
    data.append('roofStructure', roof); data.append('hasInternalBreakerPanel', String(panel === 'yes'));
    for (const [category] of evidenceFields) {
      if (panel === 'no' && (category === 'FOTO_QUADRO_INTERNO' || category === 'VIDEO_QUADRO_INVERSOR')) continue;
      if (files[category]) data.append(category, files[category]);
      else if (!study.evidences?.some((e: any) => e.category === category)) { setError('Envie todos os arquivos obrigatórios.'); return; }
    }
    setBusy(true);
    try {
      const result: any = await BaseApi.post(`api/proposal/${proposalId}/viability`, data);
      if (advanceToPayment) { window.location.reload(); return; }
      setStudy(result); setFiles({}); setId(submissionId());
      setSuccess('Dados do local atualizados com sucesso.'); setBusy(false);
    }
    catch (e) { setError(Modal.getErrorMessageText(e)); setBusy(false); }
  }
  if (study?.collectionStatus === 'ENVIADA') return <div>
    {study.pendings?.some((p: any) => p.statusCode === 1 || p.statusCode === 4)
      ? <ViabilityCorrectionForm proposalId={proposalId} initialStudy={study} onUpdated={setStudy} />
      : <ViabilityPendingPanel study={study} />}
    <p>{advanceToPayment ? 'Os dados do local já foram recebidos. Continue para o pagamento do sinal.' : 'A edição está disponível somente para arquivos com pendência de correção.'}</p>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {advanceToPayment && <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>{busy ? 'Continuando...' : 'Continuar para pagamento do sinal'}</button>}
  </div>;
  return <form onSubmit={submit}>
    <p>{advanceToPayment ? <>Viabilidade Rápida: <strong>R$ 250,00</strong>. Envie os dados do local para continuar ao pagamento do sinal.</> : 'Confira os dados do local e substitua os arquivos que precisarem de correção.'}</p>
    <p><a href={`${process.env.PUBLIC_URL || ''}/manual-viabilidade.pdf`} target="_blank" rel="noopener noreferrer">Ver manual com exemplos de fotos e vídeos</a></p>
    {error && <div role="alert" className="alert alert-danger">{error}</div>}
    {success && <div role="status" className="alert alert-success">{success}</div>}
    {!study ? <p>Carregando estudo...</p> : <fieldset disabled={busy || !study.canEdit}>
      {!study.canEdit && <div role="alert" className="alert alert-warning">{study.blockReason || 'Este estudo não permite alterações.'}</div>}
      <p>Use a localização fixa de onde a usina será instalada. Utilize a localização do dispositivo somente se estiver no local.</p>
      <div className="d-flex flex-wrap gap-2 mb-2">
        <button type="button" className="btn btn-outline-primary" onClick={locate}>Usar minha localização atual</button>
        <button type="button" className="btn btn-outline-primary" aria-expanded={showAddressSearch} onClick={() => setShowAddressSearch(value => !value)}>Buscar por endereço</button>
      </div>
      {showAddressSearch && <AddressSearch onSelect={address => {
        setLatitude(String(address.latitude)); setLongitude(String(address.longitude)); changed(); setError('');
      }} />}
      <label className="d-block">Latitude<input className="form-control" type="number" step="any" min="-90" max="90" required value={latitude} onChange={e => { setLatitude(e.target.value); changed(); }} /></label>
      <label className="d-block mb-3">Longitude<input className="form-control" type="number" step="any" min="-180" max="180" required value={longitude} onChange={e => { setLongitude(e.target.value); changed(); }} /></label>
      <label className="d-block mb-3">Estrutura do telhado<select className="form-select" required value={roof} onChange={e => { setRoof(e.target.value); changed(); }}>
        <option value="">Selecione</option><option value="MADEIRA">Madeira</option><option value="METALICO">Metálica</option><option value="CONCRETO">Concreto</option>
      </select></label>
      <label className="d-block mb-3">Existe quadro de disjuntores interno?<select className="form-select" required value={panel} onChange={e => { setPanel(e.target.value); changed(); }}>
        <option value="">Selecione</option><option value="yes">Sim</option><option value="no">Não</option>
      </select></label>
      {evidenceFields.filter(([category]) => panel !== 'no' || (category !== 'FOTO_QUADRO_INTERNO' && category !== 'VIDEO_QUADRO_INVERSOR')).map(([category, label, help]) => {
        const existing = study.evidences?.find((e: any) => e.category === category);
        return <EvidenceUpload key={category} category={category} label={label} help={help} existing={existing} file={files[category]}
          onError={setError} onChange={file => {
            setFiles(previous => { const next = { ...previous }; if (file) next[category] = file; else delete next[category]; return next; }); changed();
          }} />;
      })}
      <button className="btn btn-primary mt-3" type="submit">{busy ? 'Salvando dados...' : advanceToPayment ? 'Concluir dados e seguir para pagamento do sinal' : 'Salvar alterações'}</button>
    </fieldset>}
  </form>;
}

function InitialLocalDataAction({ proposal }: { proposal: any }) {
  const autoOpen = true;
  function open() {
    Modal.show({ title: 'Dados do Local', body: <LocalDataForm proposalId={proposal.id} advanceToPayment={autoOpen} />,
      showCancel: true, showConfirm: false, modalProps: { size: 'lg' } });
  }
  useEffect(() => {
    if (autoOpen) Modal.show({ title: 'Dados do Local', body: <LocalDataForm proposalId={proposal.id} advanceToPayment={autoOpen} />,
      showCancel: true, showConfirm: false, modalProps: { size: 'lg' } });
  }, [proposal.id, autoOpen]);
  return <button type="button" className="btn btn-primary" onClick={open}>{autoOpen ? 'Enviar dados do local' : 'Consultar ou alterar dados do local'}</button>;
}

export default function LocalDataAction({ proposal, autoOpen = true }: { proposal: any; autoOpen?: boolean }) {
  if (autoOpen) return <InitialLocalDataAction proposal={proposal} />;
  return <ViabilityPendingAction proposalId={proposal.id} onOpenUploads={() => {
    Modal.show({ title: 'Dados do Local — imagens e vídeos',
      body: <LocalDataForm proposalId={proposal.id} advanceToPayment={false} />,
      showCancel: true, showConfirm: false, modalProps: { size: 'lg' } });
  }} />;
}
